import History from './History';
import LoadingPlaceholder from 'components/LoadingPlaceholder';
import * as React from 'react';
import { connect } from 'react-redux';
import * as registrationsDispatch from 'redux/ducks/data/registrations';
import * as settingsDispatch from 'redux/ducks/settings';
import {
  DataRegistrationActions,
  fetchRegistrations,
  FetchRegistrationsOptions,
  getRegistrations,
  IPatchObject
} from 'redux/ducks/data/registrations';
import moment from 'moment';
import { RootState } from 'redux/rootReducer';
import { ThunkDispatch } from 'redux-thunk';
import { SavedSettings } from 'redux/ducks/settings';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type HistoryContainerProps = StateProps & DispatchProps;

const FixedRangeQuery = (): FetchRegistrationsOptions => ({
  createdAt: {
    from: moment().subtract(1, 'year').startOf('day').toISOString(),
    to: moment().endOf('day').toISOString()
  }
});

const HistoryContainer: React.FunctionComponent<HistoryContainerProps> = ({
  fetchRegistrations,
  deleteRegistration,
  updateRegistration,
  loading,
  registrations,
  registrationPointsMap,
  lastSyncTimestamp,
  enableLockedRegistrations,
  maxRegistrationAmount,
  showFullPath,
  updateSettings
}) => {
  // also whenever offline data changes
  React.useEffect(() => {
    void fetchRegistrations(FixedRangeQuery());
  }, [lastSyncTimestamp]);

  const handleDelete = (id: string) => {
    void deleteRegistration(id);
  };

  const handleUpdate = (id: string, patch: IPatchObject[]) => {
    void updateRegistration(id, patch);
  };

  const handleUpdateSettings = (toggleFullRegistrationPath: boolean) => {
    void updateSettings({
      showFullRegistrationPath: toggleFullRegistrationPath
    });
  };

  if (loading) {
    return <LoadingPlaceholder />;
  }

  return (
    <History
      registrations={registrations}
      deleteHandler={handleDelete}
      updateHandler={handleUpdate}
      registrationPointsMap={registrationPointsMap}
      lockedDays={enableLockedRegistrations}
      maxRegistrationAmount={maxRegistrationAmount}
      showFullPath={showFullPath}
      toggleFullPath={handleUpdateSettings}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  const { data } = state;
  const { registrations, registrationPoints } = data;
  const loading =
    registrations.loading || registrationPoints.initializing || registrationPoints.treeInitializing;
  return {
    lastSyncTimestamp: state.offline.lastSyncTimestamp,
    loading,
    registrationPoints: state.data.registrationPoints.allNodes,
    registrations: getRegistrations(state),
    registrationPointsMap: state.data.registrationPoints.registrationPointsMap,
    enableLockedRegistrations: state.settings.enableLockedRegistrations,
    maxRegistrationAmount: state.settings.maxRegistrationAmount,
    showFullPath: state.settings.showFullRegistrationPath
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, void, DataRegistrationActions>) => ({
  fetchRegistrations: (options?: FetchRegistrationsOptions) =>
    dispatch(fetchRegistrations(options)),
  deleteRegistration: (id: string) => dispatch(registrationsDispatch.remove(id)),
  updateRegistration: (id: string, patch: IPatchObject[]) =>
    dispatch(registrationsDispatch.update(id, patch)),
  updateSettings: (data: Partial<SavedSettings>) => dispatch(settingsDispatch.update(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(HistoryContainer);
